import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import { stringify } from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import RideLogo from '@/assets/icons/ride-logo.svg';
import Link from '@/components/switchback/Link/Link';
import {
  ModularListingTile,
  ModularListingTileContent,
  ModularListingTileContentShort,
  ModularListingTileMedia,
} from '@/components/ui/ModularListingTile';
import { ESearchFilters } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useRideEntryPointImpression } from '@/hooks/useRideEntryPointImpression';
import {
  trackListingImageViewedEvent,
  trackListingSelectedEvent,
} from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import {
  ERideTransitionPlacement,
  trackRideTransitionSelectedEvent,
} from '@/services/analytics/ride';
import {
  trackRecommendationTabSelected,
  trackViewAllRentalsClicked,
} from '@/services/analytics/search';
import { ESearchSource } from '@/services/analytics/search/types';
import { EDeliveryOption } from '@/services/types/core/delivery.types';
import { getRideBaseUrl } from '@/utility/getRideBaseUrl';
import { IRentalTile } from '@/utility/mapSearchResultToTile';

import css from './Recommendations.module.css';
import { ETagSlug, Tags } from './Tags';
import { ERecommendationsMode, useRecommendations } from './useRecommendations';

interface IRecommendationsProps {
  myIPAddress?: string;
  mode?: ERecommendationsMode;
}

export const Recommendations = ({
  myIPAddress,
  mode = ERecommendationsMode.RENTALS,
}: IRecommendationsProps) => {
  const { isAboveDesktop, isAboveTablet } = useBreakpoint();

  const isDeliveryMode = mode === ERecommendationsMode.DELIVERY;

  const [activeTag, setActiveTag] = useState(ETagSlug.GuestFavorite);
  const recommendationsInitiallyTriggeredRef = useRef(false);

  const isCarsTag = activeTag === ETagSlug.Cars;

  const listingSource = isDeliveryMode
    ? EListingSource.DELIVERY_RECOMMENDATIONS
    : EListingSource.HOMEPAGE_RECOMMENDATIONS;

  const recommendations = useRecommendations(activeTag, listingSource, mode);

  useEffect(() => {
    if (recommendations.loading) {
      return;
    }

    if (!recommendationsInitiallyTriggeredRef.current) {
      // avoid triggering the event when first loading the recommendations
      // with the default tag
      recommendationsInitiallyTriggeredRef.current = true;
      return;
    }

    // trigger the event after recommendations are loaded
    // to get the correct geoLocation
    trackRecommendationTabSelected({
      tag: activeTag,
      source: isDeliveryMode
        ? ESearchSource.DELIVERY_RECOMMENDATIONS
        : ESearchSource.HOMEPAGE_RECOMMENDATIONS,
      geoLocation: recommendations.location || 'USA',
      searcherGeoLocation: myIPAddress || '',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations.loading, activeTag, isDeliveryMode]);

  const onClickRental = (rental: IRentalTile, index: number) => {
    trackListingSelectedEvent({
      rental,
      eventSource: listingSource,
      listingPlacement: index + 1,
      tab: activeTag,
    });

    if (isCarsTag && rental.linkUrl) {
      trackRideTransitionSelectedEvent({
        placement: ERideTransitionPlacement.RECOMMENDATIONS_TILE,
        destinationUrl: rental.linkUrl,
        rentalID: rental.id,
      });
    }
  };

  const onChangeImage = (rental: IRentalTile, index: number, nextIndex: number) => {
    trackListingImageViewedEvent(rental, listingSource, index + 1, nextIndex + 1);
  };

  const getViewAllHref = () => {
    if (isCarsTag) {
      // ride.auto has no SERP route yet — send view-all traffic to its homepage
      return `${getRideBaseUrl()}/?utm_source=outdoorsy&utm_medium=homepage&utm_campaign=recommendations`;
    }

    if (isDeliveryMode) {
      return `/rv-search?${stringify({
        [ESearchFilters.DELIVERY]: true,
        [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.STATIONARY,
        ...(recommendations.location && { address: recommendations.location }),
      })}`;
    }

    return `/rv-search?${stringify({
      address: recommendations.location,
    })}`;
  };

  const viewAllHref = getViewAllHref();

  // One tile impression per grid, taken from the first tile, rather than one per car.
  const firstTile = recommendations.tiles[0];
  const firstRideTileUrl =
    isCarsTag && firstTile && 'linkUrl' in firstTile ? firstTile.linkUrl : undefined;
  const tileImpressionRef = useRideEntryPointImpression(
    firstRideTileUrl
      ? {
          placement: ERideTransitionPlacement.RECOMMENDATIONS_TILE,
          destinationUrl: firstRideTileUrl,
        }
      : null,
  );
  const viewAllImpressionRef = useRideEntryPointImpression(
    isCarsTag
      ? {
          placement: ERideTransitionPlacement.RECOMMENDATIONS_VIEW_ALL,
          destinationUrl: viewAllHref,
        }
      : null,
  );
  // Rare outside dense metros: the link needs 12+ cars near the visitor.
  const locationLinkImpressionRef = useRideEntryPointImpression(
    isCarsTag && recommendations.location
      ? {
          placement: ERideTransitionPlacement.RECOMMENDATIONS_LOCATION_LINK,
          destinationUrl: viewAllHref,
        }
      : null,
  );

  const buildHeading2ndLine = () => {
    if (recommendations.location)
      return (
        <FormattedMessage
          defaultMessage="near {location}"
          values={{
            location: (
              <Link href={viewAllHref} legacy passHref>
                <a
                  ref={isCarsTag ? locationLinkImpressionRef : undefined}
                  href={viewAllHref}
                  onClick={
                    isCarsTag
                      ? () =>
                          trackRideTransitionSelectedEvent({
                            placement: ERideTransitionPlacement.RECOMMENDATIONS_LOCATION_LINK,
                            destinationUrl: viewAllHref,
                          })
                      : undefined
                  }
                  className="underline hover:no-underline underline-offset-4 decoration-2">
                  {recommendations.location}
                </a>
              </Link>
            ),
          }}
          id="MtFB8P"
        />
      );

    return (
      (recommendations.loading && <>&nbsp;</>) || (
        <FormattedMessage defaultMessage="in the country" id="+Gb73f" />
      )
    );
  };

  // To avoid showing the fallback value for the second line of the heading every
  // single time we are loading next set of recommendations, we are going to use
  // a state to store the value of the second line value, and update it only after
  // the recommendations are loaded.
  const [heading2ndLine, setHeading2ndLine] = useState(() => buildHeading2ndLine());

  useEffect(() => {
    if (!recommendations.loading) setHeading2ndLine(buildHeading2ndLine());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations]);

  const handleViewAllClick = () => {
    const eventData = {
      tab: activeTag,
      source: isDeliveryMode ? 'delivery-recommendations' : 'homepage-recommendations',
      geoLocation: recommendations.location || 'USA',
      searcherGeoLocation: myIPAddress || '',
    };

    trackViewAllRentalsClicked(eventData);

    if (isCarsTag) {
      trackRideTransitionSelectedEvent({
        placement: ERideTransitionPlacement.RECOMMENDATIONS_VIEW_ALL,
        destinationUrl: viewAllHref,
      });
    }
  };

  const handleTagChange = (tag: ETagSlug) => {
    setActiveTag(tag);
  };

  const getHeadingContent = () => {
    if (isCarsTag) {
      return (
        <FormattedMessage
          id="iFzlDE"
          defaultMessage="Find the best car rentals"
          description="Homepage recommendations heading when the Cars tag is active"
        />
      );
    }

    if (isDeliveryMode) {
      return <FormattedMessage defaultMessage="Find the best stationary RVs" id="+8P8BI" />;
    }

    return <FormattedMessage defaultMessage="Find the best RV rentals" id="DaGido" />;
  };

  const getDescriptionContent = () => {
    if (isCarsTag) {
      return (
        <FormattedMessage
          id="+cU5kP"
          defaultMessage="Rent a car for your next trip from local hosts on Ride – An Outdoorsy Company."
          description="Homepage recommendations description when the Cars tag is active"
        />
      );
    }

    if (isDeliveryMode) {
      return (
        <FormattedMessage defaultMessage="Then kick your feet up. We've got this." id="ieYaWk" />
      );
    }

    return (
      <FormattedMessage
        defaultMessage="Explore the open road in the perfect RV rental. Or, get one delivered and set up for you."
        id="yq8PYC"
      />
    );
  };

  const getButtonLabel = () => {
    if (isCarsTag) {
      return (
        <FormattedMessage
          id="s83+jk"
          defaultMessage="View all cars on Ride"
          description="Homepage recommendations view-all button when the Cars tag is active"
        />
      );
    }

    if (isDeliveryMode) {
      return <FormattedMessage defaultMessage="View all stationary RVs" id="hcg97R" />;
    }
    return (
      <FormattedMessage
        defaultMessage="View all<desktop> rentals</desktop>"
        values={{
          desktop: value => (
            <span aria-hidden className="hidden md:inline">
              {value}
            </span>
          ),
        }}
        id="0EFHuC"
      />
    );
  };

  const getGridClassName = () => {
    const baseClasses = `grid gap-4 lg:gap-x-8 lg:grid-cols-3 xxxxl:grid-cols-4 ${css.grid}`;

    if (isDeliveryMode) {
      return `${baseClasses} ${css.deliveryGrid} grid-cols-1`;
    }

    return `${baseClasses} grid-cols-2`;
  };

  return (
    <div className="flex flex-col px-6 mx-auto max-w-xxxxl lg:px-20">
      <div className="flex flex-col gap-4 lg:gap-6 secton-animated-bilock">
        <div>
          <Heading variant={EHeadingStyleVariant.H1Alt} className={css.heading}>
            {getHeadingContent()}
          </Heading>

          {!isDeliveryMode && (
            <Heading component="div" variant={EHeadingStyleVariant.H1Alt} className={css.heading}>
              {heading2ndLine}
            </Heading>
          )}
        </div>

        <Text variant={ETextStyleVariant.LargeRegular} className={css.description}>
          {getDescriptionContent()}
        </Text>
      </div>

      <Tags active={activeTag} onChange={handleTagChange} mode={mode} />

      <div className="flex flex-col gap-4 lg:gap-16 section-animated-block">
        <div className={getGridClassName()}>
          {recommendations.tiles.map((rentalTile, index) => (
            <ModularListingTile
              key={`${activeTag}-${index}`}
              rentalTile={rentalTile}
              target={isAboveDesktop ? '_blank' : undefined}
              onClick={() => onClickRental(rentalTile, index)}>
              <div ref={index === 0 ? tileImpressionRef : undefined} className="relative">
                <ModularListingTileMedia
                  autoRotateOnHover
                  rentalTile={rentalTile}
                  {...(recommendations.loading
                    ? {}
                    : {
                        largeSourceSize: false,
                        onSlideChange: nextIndex => onChangeImage(rentalTile, index, nextIndex),
                      })}
                  className="aspect-square md:aspect-[407/236] !h-auto"
                  showSuperhost
                  wishlistingEventData={{
                    index,
                    eventSource: listingSource,
                  }}
                  showGuestFavorite
                  hideTagOnSmallScreen
                />

                {isCarsTag && !recommendations.loading && (
                  <span className="absolute z-10 flex items-center text-gray-900 bg-white rounded-full px-2.5 py-1.5 shadow-sm top-3 left-3">
                    <RideLogo role="img" aria-label="Ride" className="w-auto h-3.5" />
                  </span>
                )}
              </div>

              <div aria-hidden={!isAboveTablet} className="hidden md:block">
                <ModularListingTileContent rentalTile={rentalTile} />
              </div>

              <div aria-hidden={isAboveTablet} className="md:hidden">
                <ModularListingTileContentShort rentalTile={rentalTile} />
              </div>
            </ModularListingTile>
          ))}
        </div>

        <Link href={viewAllHref} legacy passHref>
          <Button
            ref={viewAllImpressionRef}
            variant={EButtonColorVariant.Tertiary}
            label={getButtonLabel()}
            href={viewAllHref}
            className="self-center"
            onClick={handleViewAllClick}
          />
        </Link>
      </div>
    </div>
  );
};
